import { useContext, useEffect, useState } from 'react'
import { InternalContext } from '../../contexts/InternalContext'
import { CutterContext } from '../../contexts/CutterContext'
import { zoomPower } from '../functions/zoomPower'
import { useGetCachedReducedData } from './useGetCachedReducedData'
import { useCutSource } from './useCutSource'
import { reduce } from '../functions/reduce'
import { useMaxCachedReducedData } from './useMaxCachedReducedData'

export interface ChannelCanvasData {
  data: Float32Array
  offset: number
  scaleFactor: number
  samplesPerPixel: number
}

interface Props {
  source: Float32Array
  canvasWidth: number
  zoomStep?: number
}

export function useChannelCanvasData({ source, canvasWidth, zoomStep = 2 }: Props): ChannelCanvasData | null {
  const { zoom } = useContext(InternalContext)
  const { visibleRange, audio } = useContext(CutterContext)
  const cutSource = useCutSource(source)
  const maxCachedReducedData = useMaxCachedReducedData({ source, zoomStep })
  const getCachedReducedData = useGetCachedReducedData({ maxCachedReducedData, zoomStep, canvasWidth })
  const [canvasData, setCanvasData] = useState<ChannelCanvasData | null>(null)

  useEffect(() => {
    const roundedZoom = zoomPower(zoom, zoomStep)
    const visibleLength = visibleRange[1] - visibleRange[0]
    const cachedData = getCachedReducedData(roundedZoom)

    if (cachedData) {
      const reducingFactor = audio.length / cachedData.length

      setCanvasData({
        data: cachedData,
        offset: visibleRange[0] / reducingFactor,
        scaleFactor: visibleLength / (reducingFactor * canvasWidth),
        samplesPerPixel: reducingFactor,
      })
    } else {
      const data = canvasWidth * roundedZoom > maxCachedReducedData.length ? cutSource : maxCachedReducedData
      const reducingFactor = data.length / audio.length
      const roundedStep = Math.ceil(data.length / roundedZoom)
      const start = Math.floor((visibleRange[0] * reducingFactor) / roundedStep) * roundedStep
      const end = Math.ceil((visibleRange[1] * reducingFactor) / roundedStep) * roundedStep

      const dataSliced = data.subarray(start, end)
      const dataReducedLength = canvasWidth * zoomStep
      const dataReduced = reduce(dataSliced, dataReducedLength)
      const samplesPerPixel = dataSliced.length / (dataReducedLength * reducingFactor)

      setCanvasData({
        data: dataReduced,
        offset: (visibleRange[0] * reducingFactor - start) * (dataReducedLength / dataSliced.length),
        scaleFactor: visibleLength / (samplesPerPixel * canvasWidth),
        samplesPerPixel,
      })
    }
  }, [visibleRange, zoom, maxCachedReducedData, audio, canvasWidth, source, zoomStep, getCachedReducedData, cutSource])

  return canvasData
}
